import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useEffect, useState, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Plus } from 'lucide-react';
import { fetchEmpresasConfianza, EmpresaConfianza } from '../../../services/empresaConfianzaService';
import { AddEmpresasConfianzaPage } from '../../../components/EmpresaConfianza/AddEmpresasConfianzaPage';
import { EditEmpresasConfianzaPage } from '../../../components/EmpresaConfianza/EditEmpresasConfianzaPage';
import { DeleteEmpresasConfianzaPage } from '../../../components/EmpresaConfianza/DeleteEmpresasConfianzaPage';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const EmpresasConfianzaPage: React.FC = () => {
  const [empresas, setEmpresas] = useState<EmpresaConfianza[]>([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState<EmpresaConfianza | null>(null);
  const [empresaToDelete, setEmpresaToDelete] = useState<EmpresaConfianza | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
  const { logout, user } = useAuth();

  const userLevelId = user?.user_level_id ?? 0;
  const perm = useModelPermission('empresaconfianza', userLevelId); // Ajusta el nombre del modelo según tu backend

  const loadEmpresas = useCallback(async () => {
    if (!perm?.can_read) {
      setEmpresas([]);
      return;
    }
    try {
      const data = await fetchEmpresasConfianza();
      setEmpresas(data.sort((a, b) => a.id - b.id));
    } catch (error) {
      /* Browser diagnostics omitted. */
      setEmpresas([]);
    }
  }, [perm?.can_read]);

  useEffect(() => {
    loadEmpresas();
  }, [loadEmpresas]);

  const handleDeleteSuccess = () => {
    setEmpresas(prev => prev.filter(e => e.id !== empresaToDelete?.id));
    setEmpresaToDelete(null);
  };

  const handleAddSuccess = () => {
    loadEmpresas(); // Recarga completa después de agregar
  };

  const handleEditSuccess = () => {
    loadEmpresas(); // Recarga completa después de editar
  };

  // Paginación
  const totalPages = Math.ceil(empresas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedEmpresas = empresas.slice(startIndex, startIndex + itemsPerPage);

  if (perm === null) return <Loading text="Cargando empresas de confianza..." />;

  return (
    <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Empresas de Confianza</h2>
          <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
            <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
            <span>/</span>
            <a href="/empresas-confianza" className="text-[#c1348b] hover:text-[#8f1f64]">Empresas de Confianza</a>
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <button
            onClick={loadEmpresas}
            className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors"
            title="Recargar empresas"
          >
            <History className="w-5 h-5" />
          </button>
          {perm.can_create && (
            <button
              style={{ minWidth: '170.75px', height: '39px' }}
              onClick={() => setIsAddOpen(true)}
              className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center text-sm font-medium transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Agregar Empresa
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Modales */}
        <AddEmpresasConfianzaPage
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          onSuccess={handleAddSuccess}
        />

        {selectedEmpresa && (
          <EditEmpresasConfianzaPage
            isOpen={!!selectedEmpresa}
            onClose={() => setSelectedEmpresa(null)}
            empresa={selectedEmpresa}
            onSuccess={handleEditSuccess}
          />
        )}

        {empresaToDelete && (
          <DeleteEmpresasConfianzaPage
            isOpen={!!empresaToDelete}
            onClose={() => setEmpresaToDelete(null)}
            empresa={empresaToDelete}
            onSuccess={handleDeleteSuccess}
          />
        )}

        <ExpiredSessionModal
          isOpen={isSessionExpired}
          onClose={async () => {
            setIsSessionExpired(false);
            await logout();
          }}
        />

        <div className="overflow-x-auto">
          <AdminTable className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rubro</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orden</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {perm.can_read && displayedEmpresas.map((empresa) => (
                <tr key={empresa.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-sm text-gray-900">{empresa.nombre}</td>
                  <td className="px-4 py-4 text-sm text-gray-900">{empresa.rubro}</td>
                  <td className="px-4 py-4 text-sm text-gray-500 max-w-xs truncate">{empresa.descripcion}</td>
                  <td className="px-4 py-4 text-sm text-gray-500">{empresa.orden}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2 inline-flex text-xs font-medium rounded-full ${empresa.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {empresa.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex gap-2">
                      {perm.can_update && (
                        <button
                          onClick={() => setSelectedEmpresa(empresa)}
                          className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                          title="Editar empresa"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {perm.can_delete && (
                        <button
                          onClick={() => setEmpresaToDelete(empresa)}
                          className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                          title="Eliminar empresa"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {perm.can_read && empresas.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <span className="text-lg font-medium text-gray-600">No hay empresas registradas</span>
                      {perm.can_create && (
                        <p className="text-gray-500 text-sm">Agrega nuevas empresas para comenzar</p>
                      )}
                    </div>
                  </td>
                </tr>
              )}

              {!perm.can_read && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <span className="text-gray-500">No tienes permiso para ver empresas de confianza</span>
                  </td>
                </tr>
              )}
            </tbody>
          </AdminTable>
        </div>

        {perm.can_read && empresas.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={empresas.length}
          />
        )}
      </div>
    </div>
  );
};

export default EmpresasConfianzaPage;
